import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, 
  Clipboard, 
  Brain, 
  FileText, 
  CheckCircle2, 
  Loader2, 
  Trash2,
  Eye,
  Copy,
  AlertTriangle,
  Sparkles,
  Target,
  BarChart3,
  Image,
  Twitter,
  Undo2,
  Download,
  Newspaper,
  ClipboardCheck,
  TrendingUp,
  ExternalLink,
  RefreshCw,
  Trophy,
  MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommandCenter() {
  const navigate = useNavigate();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('intelligence');
  
  // Intelligence Lab State
  const [pastedImage, setPastedImage] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Workflow State
  const [workflow, setWorkflow] = useState(null);
  
  // Victory Lap State
  const [victoryLapLoading, setVictoryLapLoading] = useState(false);
  const [victoryLapData, setVictoryLapData] = useState(null);
  
  // Scripts State
  const [scripts, setScripts] = useState([]);
  const [selectedReportsForScript, setSelectedReportsForScript] = useState([]);
  const [generatingScript, setGeneratingScript] = useState(false);
  
  // Twitter Repurpose State
  const [twitterTranscript, setTwitterTranscript] = useState('');
  const [twitterArticle, setTwitterArticle] = useState(null);
  const [generatingTwitter, setGeneratingTwitter] = useState(false);
  
  // Articles Division State
  const [articlesTwitterContent, setArticlesTwitterContent] = useState('');
  const [articlesYoutubeContent, setArticlesYoutubeContent] = useState('');
  const [articlesPlayerName, setArticlesPlayerName] = useState('');
  const [articlesOpponent, setArticlesOpponent] = useState('');
  const [articlesStatType, setArticlesStatType] = useState('');
  const [seoArticle, setSeoArticle] = useState(null);
  const [generatingSeoArticle, setGeneratingSeoArticle] = useState(false);
  const [publishingArticle, setPublishingArticle] = useState(false);
  const [articleFeaturedImage, setArticleFeaturedImage] = useState('');
  
  // Article Rewrite State
  const [rewriteContent, setRewriteContent] = useState('');
  const [rewriteDate, setRewriteDate] = useState('');
  const [rewrittenArticle, setRewrittenArticle] = useState(null);
  const [rewritingArticle, setRewritingArticle] = useState(false);
  
  // Screenshot Capture State
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [capturedScreenshot, setCapturedScreenshot] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  
  // Thumbnail State - Enhanced Version
  const [thumbnailStyle, setThumbnailStyle] = useState('3');
  const [thumbnailHeadline, setThumbnailHeadline] = useState('FREE PICKS');
  const [thumbnailSubtext, setThumbnailSubtext] = useState('NBA THURSDAY');
  const [thumbnailPlayer, setThumbnailPlayer] = useState('');
  const [thumbnailStat, setThumbnailStat] = useState('');
  const [thumbnailImageUrl, setThumbnailImageUrl] = useState('');
  const [thumbnailImageServerId, setThumbnailImageServerId] = useState(''); // Server-stored image ID for full-size URL
  const [thumbnailPlayerSearch, setThumbnailPlayerSearch] = useState('');
  const [showThumbnailPlayerDropdown, setShowThumbnailPlayerDropdown] = useState(false);
  const [showThumbnailImageSearch, setShowThumbnailImageSearch] = useState(false);
  const [thumbnailImageResults, setThumbnailImageResults] = useState([]);
  const [showThumbnailReportParser, setShowThumbnailReportParser] = useState(false);
  const [thumbnailReportText, setThumbnailReportText] = useState('');
  const [thumbnailParseStatus, setThumbnailParseStatus] = useState('');
  const [thumbnailRecord, setThumbnailRecord] = useState({ wins: 0, losses: 0, pending: 0 });
  const [thumbnailImageEffect, setThumbnailImageEffect] = useState('glow');
  const [thumbnailOutputSize, setThumbnailOutputSize] = useState('youtube');
  const [thumbnailSponsorLogo, setThumbnailSponsorLogo] = useState('prizepicks');
  const [thumbnailLogoPosition, setThumbnailLogoPosition] = useState('bottom-right');
  
  // Sponsor logo URLs
  const sponsorLogos = {
    prizepicks: 'https://customer-assets.emergentagent.com/job_091dd62f-8598-45b9-9f7f-cf6171d80391/artifacts/178c9qct_pp2.PNG',
    prizepicks_light: 'https://customer-assets.emergentagent.com/job_091dd62f-8598-45b9-9f7f-cf6171d80391/artifacts/aeyq8ixl_pp1.png',
    novig: 'https://customer-assets.emergentagent.com/job_091dd62f-8598-45b9-9f7f-cf6171d80391/artifacts/kromgfqv_400x400%20image-novig.png',
    none: ''
  };
  
  // NBA Players list for search
  const NBA_PLAYERS = [
    "LeBron James", "Stephen Curry", "Kevin Durant", "Giannis Antetokounmpo", "Luka Doncic",
    "Jayson Tatum", "Joel Embiid", "Nikola Jokic", "Ja Morant", "Damian Lillard",
    "Anthony Edwards", "Devin Booker", "Trae Young", "Donovan Mitchell", "Kyrie Irving",
    "Zion Williamson", "Anthony Davis", "Paul George", "Kawhi Leonard", "Jimmy Butler",
    "Bam Adebayo", "Tyrese Haliburton", "De'Aaron Fox", "Shai Gilgeous-Alexander", "Chet Holmgren",
    "Victor Wembanyama", "Paolo Banchero", "Scottie Barnes", "Evan Mobley", "Cade Cunningham",
    "Jalen Green", "Tyrese Maxey", "Desmond Bane", "Jaren Jackson Jr", "Darius Garland",
    "Fred VanVleet", "Jalen Brunson", "Julius Randle", "RJ Barrett", "OG Anunoby",
    "Pascal Siakam", "Myles Turner", "Buddy Hield", "CJ McCollum", "Brandon Ingram",
    "Zach LaVine", "DeMar DeRozan", "Khris Middleton", "Brook Lopez", "Bobby Portis",
    "Lauri Markkanen", "Jordan Clarkson", "Collin Sexton", "Anfernee Simons", "Jerami Grant",
    "Domantas Sabonis", "Keegan Murray", "Franz Wagner", "Jalen Williams", "Josh Giddey",
    "Alperen Sengun", "Jabari Smith Jr", "Amen Thompson", "Ausar Thompson", "Cam Thomas",
    "Mikal Bridges", "Cameron Johnson", "Spencer Dinwiddie", "Derrick White", "Jaylen Brown",
    "Al Horford", "Marcus Smart", "Robert Williams III", "Karl-Anthony Towns", "Rudy Gobert",
    "Mike Conley", "Naz Reid", "Jaden McDaniels", "Austin Reaves", "D'Angelo Russell",
    "Rui Hachimura", "Jarred Vanderbilt", "Terry Rozier", "LaMelo Ball", "Miles Bridges"
  ].sort();
  
  // Results Audit State
  const [auditPlayerName, setAuditPlayerName] = useState('');
  const [auditProp, setAuditProp] = useState('');
  const [auditFinalStat, setAuditFinalStat] = useState('');
  const [auditMode, setAuditMode] = useState('screenshot');
  const [auditResult, setAuditResult] = useState(null);
  const [auditingResult, setAuditingResult] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditScreenshot, setAuditScreenshot] = useState(null);
  
  // Growth Lab State
  const [selectedScriptForGrowth, setSelectedScriptForGrowth] = useState(null);
  const [growthReview, setGrowthReview] = useState(null);
  const [generatingGrowth, setGeneratingGrowth] = useState(false);
  const [selectedTitleIndex, setSelectedTitleIndex] = useState(0);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReports();
      fetchWorkflow();
      fetchScripts();
      fetchThumbnailRecord();
      fetchAuditData();
    }
  }, [isAuthenticated]);

  // Clipboard paste handler
  useEffect(() => {
    const handlePaste = async (e) => {
      if (activeTab !== 'intelligence') return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              setPastedImage(event.target.result);
              toast.success('Image pasted! Click "Analyze" to process.');
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [activeTab]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/command-center/intelligence-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const fetchWorkflow = async () => {
    try {
      const response = await axios.get(`${API}/command-center/workflow/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflow(response.data);
    } catch (error) {
      console.error('Failed to fetch workflow:', error);
    }
  };

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${API}/command-center/scripts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to fetch scripts:', error);
    }
  };

  const fetchThumbnailRecord = async () => {
    try {
      const response = await axios.get(`${API}/results/summary`);
      const { wins, losses, pushes } = response.data;
      setThumbnailRecord({ wins, losses, pending: pushes });
    } catch (error) {
      console.error('Failed to fetch thumbnail record:', error);
    }
  };

  // Filter players based on search
  const filteredThumbnailPlayers = NBA_PLAYERS.filter(p => 
    p.toLowerCase().includes(thumbnailPlayerSearch.toLowerCase())
  ).slice(0, 10);

  // Select player from dropdown
  const selectThumbnailPlayer = (selectedPlayer) => {
    setThumbnailPlayer(selectedPlayer.toUpperCase());
    setThumbnailPlayerSearch(selectedPlayer);
    setShowThumbnailPlayerDropdown(false);
  };

  // Search for player images
  const searchThumbnailPlayerImages = async () => {
    if (!thumbnailPlayer) return;
    
    const playerName = thumbnailPlayer.toLowerCase().replace(/\s+/g, '+');
    
    const imageSources = [
      { name: 'Google Images', url: `https://www.google.com/search?tbm=isch&q=${playerName}+nba+png+transparent`, description: 'Best for finding PNG cutouts' },
      { name: 'NBA.com', url: `https://www.nba.com/search?filters=player&q=${playerName}`, description: 'Official player headshots' },
      { name: 'ESPN', url: `https://www.espn.com/search/_/q/${playerName}`, description: 'High quality action shots' },
      { name: 'Pinterest', url: `https://www.pinterest.com/search/pins/?q=${playerName}+nba+png`, description: 'Community-made cutouts' }
    ];
    
    setThumbnailImageResults(imageSources);
    setShowThumbnailImageSearch(true);
  };

  // Parse Intelligence Report for thumbnail
  const parseThumbnailReport = () => {
    try {
      const text = thumbnailReportText;
      
      // Extract player name
      const propMatch = text.match(/PROP\s*\d*\s*[–-]\s*([A-Za-z\s]+?)\s*[–-]\s*(Under|Over)/i);
      let extractedPlayer = '';
      
      if (propMatch) {
        extractedPlayer = propMatch[1].trim().toUpperCase();
      }
      
      // Extract the line/stat
      const lineMatch = text.match(/(Under|Over)\s*(\d+\.?\d*)\s*([A-Za-z\s\+]+?)(?:\n|Market)/i);
      let extractedStat = '';
      
      if (lineMatch) {
        const direction = lineMatch[1];
        const number = lineMatch[2];
        let statType = lineMatch[3].trim();
        
        statType = statType
          .replace(/Points\s*\+\s*Assists/i, 'PTS+AST')
          .replace(/Points\s*\+\s*Rebounds/i, 'PTS+REB')
          .replace(/Points/i, 'PTS')
          .replace(/Assists/i, 'AST')
          .replace(/Rebounds/i, 'REB')
          .replace(/Threes/i, '3PM')
          .replace(/Three Pointers Made/i, '3PM')
          .replace(/Steals/i, 'STL')
          .replace(/Blocks/i, 'BLK');
        
        extractedStat = `${direction} ${number} ${statType}`;
      }
      
      // Extract date to determine day
      const dateMatch = text.match(/Date:\s*(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const date = new Date(dateMatch[1]);
        const dayName = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][date.getDay()];
        setThumbnailSubtext(`NBA ${dayName}`);
      }
      
      // Set extracted values
      if (extractedPlayer) {
        setThumbnailPlayer(extractedPlayer);
        setThumbnailPlayerSearch(extractedPlayer);
      }
      if (extractedStat) setThumbnailStat(extractedStat);
      
      // Build status message
      let status = [];
      if (extractedPlayer) status.push(`Player: ${extractedPlayer}`);
      if (extractedStat) status.push(`Stat: ${extractedStat}`);
      
      if (status.length > 0) {
        setThumbnailParseStatus(`✅ Extracted: ${status.join(' | ')}`);
      } else {
        setThumbnailParseStatus('⚠️ Could not extract data. Make sure you pasted the full report.');
      }
      
      setShowThumbnailReportParser(false);
    } catch (error) {
      setThumbnailParseStatus('❌ Error parsing report. Try again.');
    }
  };

  // Get thumbnail preview URL
  const getThumbnailPreviewUrl = () => {
    const logoUrl = sponsorLogos[thumbnailSponsorLogo] || '';
    // Use server-stored image ID if available (allows full-size page to work with uploaded images)
    // Otherwise, skip data URLs (too long) or use regular URLs
    let imageParam = '';
    if (thumbnailImageServerId) {
      // Use the server-stored image reference
      imageParam = `tempid:${thumbnailImageServerId}`;
    } else if (thumbnailImageUrl && !thumbnailImageUrl.startsWith('data:')) {
      // Regular URL, encode it
      imageParam = encodeURIComponent(thumbnailImageUrl);
    }
    return `/thumbnail?style=${thumbnailStyle}&headline=${encodeURIComponent(thumbnailHeadline)}&subtext=${encodeURIComponent(thumbnailSubtext)}&player=${encodeURIComponent(thumbnailPlayer)}&stat=${encodeURIComponent(thumbnailStat)}&image=${imageParam}&effect=${thumbnailImageEffect}&size=${thumbnailOutputSize}&logo=${encodeURIComponent(logoUrl)}&logopos=${thumbnailLogoPosition}`;
  };

  // Get image effect style
  const getImageEffectStyle = () => {
    switch (thumbnailImageEffect) {
      case 'glow':
        return { filter: 'drop-shadow(0 0 30px rgba(74,222,128,0.5)) drop-shadow(0 0 60px rgba(74,222,128,0.3))' };
      case 'shadow':
        return { filter: 'drop-shadow(8px 8px 20px rgba(0,0,0,0.8))' };
      case 'intense':
        return { filter: 'drop-shadow(0 0 40px rgba(74,222,128,0.7)) drop-shadow(0 0 80px rgba(74,222,128,0.4)) contrast(1.1) saturate(1.2)' };
      default:
        return {};
    }
  };

  const thumbnailWinRate = thumbnailRecord.wins + thumbnailRecord.losses > 0 
    ? ((thumbnailRecord.wins / (thumbnailRecord.wins + thumbnailRecord.losses)) * 100).toFixed(1)
    : '0';

  const thumbnailDays = ['NBA MONDAY', 'NBA TUESDAY', 'NBA WEDNESDAY', 'NBA THURSDAY', 'NBA FRIDAY', 'NBA SATURDAY', 'NBA SUNDAY'];

  const fetchAuditData = async () => {
    try {
      const [historyRes, summaryRes] = await Promise.all([
        axios.get(`${API}/command-center/audit-results`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/command-center/audit-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAuditHistory(historyRes.data);
      setAuditSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch audit data:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setPastedImage(event.target.result);
      toast.success('Image loaded! Click "Analyze" to process.');
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPastedImage(event.target.result);
        toast.success('Image dropped! Click "Analyze" to process.');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const analyzeImage = async () => {
    if (!pastedImage) {
      toast.error('Please paste or upload an image first');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/command-center/analyze-screenshot-base64`,
        { image_base64: pastedImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAnalysisResult(response.data);
        toast.success('Analysis complete!');
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this intelligence report?')) return;
    
    try {
      await axios.delete(`${API}/command-center/intelligence-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report deleted');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const completeWorkflowStep = async (step) => {
    try {
      await axios.post(
        `${API}/command-center/workflow/complete-step?step=${encodeURIComponent(step)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Step completed!');
      fetchWorkflow();
    } catch (error) {
      toast.error('Failed to update workflow');
    }
  };

  const uncompleteWorkflowStep = async (step) => {
    try {
      await axios.post(
        `${API}/command-center/workflow/uncomplete-step?step=${encodeURIComponent(step)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Step marked incomplete');
      fetchWorkflow();
    } catch (error) {
      toast.error('Failed to update workflow');
    }
  };

  const generateScript = async () => {
    if (selectedReportsForScript.length === 0) {
      toast.error('Select at least one report to generate a script');
      return;
    }

    setGeneratingScript(true);
    try {
      const response = await axios.post(
        `${API}/command-center/generate-script`,
        selectedReportsForScript,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Script generated!');
        fetchScripts();
        setSelectedReportsForScript([]);
      }
    } catch (error) {
      toast.error('Failed to generate script');
    } finally {
      setGeneratingScript(false);
    }
  };

  const toggleReportSelection = (reportId) => {
    setSelectedReportsForScript(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const clearImage = () => {
    setPastedImage(null);
    setAnalysisResult(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Victory Lap - Generate follow-up posts
  const generateVictoryLap = async () => {
    setVictoryLapLoading(true);
    setVictoryLapData(null);
    try {
      const response = await axios.post(`${API}/victory-lap`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVictoryLapData(response.data);
      toast.success(`Victory Lap generated! Tonight: ${response.data.tonight_record}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate posts';
      toast.error(msg);
    } finally {
      setVictoryLapLoading(false);
    }
  };

  // Quick Copy All - combines title, script, and review into one clipboard-ready block
  const copyAllGrowthContent = () => {
    if (!growthReview || !selectedScriptForGrowth) {
      toast.error('Generate a review first');
      return;
    }

    const selectedTitle = growthReview.titles?.title_options?.[selectedTitleIndex] || 'No title selected';
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    
    const fullContent = `═══════════════════════════════════════════
PSL CONTENT PACK - ${today}
═══════════════════════════════════════════

📌 SELECTED TITLE:
${selectedTitle}

═══════════════════════════════════════════
📜 FULL SCRIPT (${selectedScriptForGrowth.duration_target})
═══════════════════════════════════════════

${selectedScriptForGrowth.script_content}

═══════════════════════════════════════════
📊 GROWTH LAB REVIEW
═══════════════════════════════════════════

${growthReview.review}

═══════════════════════════════════════════
🎯 ALL TITLE OPTIONS:
${growthReview.titles?.title_options?.map((t, i) => `${i + 1}. ${t}`).join('\n') || 'No titles'}
═══════════════════════════════════════════`;

    navigator.clipboard.writeText(fullContent);
    toast.success('Full content pack copied! Ready to paste.');
  };

  // Save analysis as Intelligence Report
  const saveAsReport = async () => {
    if (!analysisResult?.raw_response) {
      toast.error('No analysis to save');
      return;
    }

    try {
      // Parse player name and prop type from the analysis
      const text = analysisResult.raw_response;
      let playerName = 'Unknown Player';
      let propType = 'Unknown';

      // Try to extract player name from PROP 1 line
      const propMatch = text.match(/PROP \d+ [-–] ([^-–]+) [-–] (.+)/i);
      if (propMatch) {
        playerName = propMatch[1].trim();
        propType = propMatch[2].trim();
      }

      // Try to find in SUBJECT line
      const subjectMatch = text.match(/SUBJECT:\s*(.+)/i);
      if (subjectMatch && playerName === 'Unknown Player') {
        playerName = subjectMatch[1].trim();
      }

      const reportData = {
        player_name: playerName,
        prop_type: propType,
        edge_summary: text.substring(0, 500),
        raw_analysis: text
      };

      await axios.post(`${API}/command-center/intelligence-reports`, reportData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Report saved! You can now select it for script generation.');
      fetchReports();
    } catch (error) {
      console.error('Failed to save report:', error);
      toast.error('Failed to save report');
    }
  };

  // Growth Lab Functions
  const generateGrowthReview = async (scriptId) => {
    setGeneratingGrowth(true);
    try {
      const response = await axios.post(
        `${API}/command-center/generate-growth-review?script_id=${scriptId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setGrowthReview(response.data);
        toast.success('Growth review generated!');
      }
    } catch (error) {
      toast.error('Failed to generate growth review');
    } finally {
      setGeneratingGrowth(false);
    }
  };

  // Twitter Repurpose Functions
  const generateTwitterArticle = async () => {
    if (!twitterTranscript.trim()) {
      toast.error('Please paste your YouTube transcript');
      return;
    }

    setGeneratingTwitter(true);
    try {
      const today = new Date();
      const dayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
      const dateStr = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      const response = await axios.post(
        `${API}/command-center/generate-twitter-article`,
        { 
          transcript: twitterTranscript,
          day_of_week: dayOfWeek,
          date: dateStr
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setTwitterArticle(response.data.content);
        toast.success('Twitter article generated!');
      }
    } catch (error) {
      toast.error('Failed to generate Twitter article');
    } finally {
      setGeneratingTwitter(false);
    }
  };

  // Articles Division Functions
  const generateSeoArticle = async () => {
    if (!articlesTwitterContent.trim() || !articlesYoutubeContent.trim()) {
      toast.error('Please provide both Twitter and YouTube content');
      return;
    }

    setGeneratingSeoArticle(true);
    try {
      const today = new Date();
      const dateStr = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      const response = await axios.post(
        `${API}/command-center/generate-seo-article`,
        { 
          twitter_content: articlesTwitterContent,
          youtube_content: articlesYoutubeContent,
          player_name: articlesPlayerName || null,
          opponent: articlesOpponent || null,
          stat_type: articlesStatType || null,
          date: dateStr
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSeoArticle(response.data);
        toast.success(`SEO Article generated! (${response.data.word_count} words)`);
      }
    } catch (error) {
      toast.error('Failed to generate SEO article');
    } finally {
      setGeneratingSeoArticle(false);
    }
  };

  // Rewrite existing article into SEO format
  const rewriteExistingArticle = async () => {
    if (!rewriteContent.trim()) {
      toast.error('Please paste an existing article to rewrite');
      return;
    }

    setRewritingArticle(true);
    try {
      const response = await axios.post(
        `${API}/command-center/rewrite-article`,
        { 
          content: rewriteContent,
          date: rewriteDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setRewrittenArticle(response.data);
        toast.success(`Article rewritten! (${response.data.word_count} words)`);
      }
    } catch (error) {
      toast.error('Failed to rewrite article');
    } finally {
      setRewritingArticle(false);
    }
  };

  // Capture screenshot of external URL
  const captureScreenshot = async () => {
    if (!screenshotUrl.trim()) {
      toast.error('Please enter a URL to capture');
      return;
    }

    setCapturingScreenshot(true);
    setCapturedScreenshot(null);
    try {
      const response = await axios.post(
        `${API}/command-center/capture-screenshot`,
        { url: screenshotUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCapturedScreenshot({
          image_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`,
          image_id: response.data.image_id,
          source_url: response.data.source_url
        });
        toast.success('Screenshot captured!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to capture screenshot');
    } finally {
      setCapturingScreenshot(false);
    }
  };

  // Publish article to website
  const publishArticleToWebsite = async () => {
    if (!seoArticle || !seoArticle.content) {
      toast.error('No article to publish');
      return;
    }

    setPublishingArticle(true);
    try {
      // Use the SEO title or generate one from the content
      const title = seoArticle.seo_title || 'New Analysis - ' + new Date().toLocaleDateString();
      
      // Clean up content for excerpt - remove SEO metadata markers
      const cleanForExcerpt = (text) => {
        let clean = text;
        // Remove SEO TITLE section
        clean = clean.replace(/##?\s*\d*\.?\s*SEO TITLE[\s\S]*?(?=##|META|\n\n)/gi, '');
        // Remove META DESCRIPTION section  
        clean = clean.replace(/##?\s*\d*\.?\s*META DESCRIPTION[\s\S]*?(?=##|INTRODUCTION|\n\n)/gi, '');
        // Remove markdown symbols
        clean = clean.replace(/[#*_]/g, '');
        // Remove extra whitespace
        clean = clean.replace(/\s+/g, ' ').trim();
        return clean;
      };
      
      const cleanedContent = cleanForExcerpt(seoArticle.content);
      const excerpt = cleanedContent.substring(0, 200).trim() + '...';

      const response = await axios.post(
        `${API}/articles`,
        { 
          title: title,
          content: seoArticle.content,
          excerpt: excerpt,
          featured_image: articleFeaturedImage || null,
          published: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Article published to your website!');
      
      // Open the article in a new tab
      if (response.data.slug) {
        window.open(`/article/${response.data.slug}`, '_blank');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish article: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishingArticle(false);
    }
  };

  // Results Audit Functions
  const auditPropResult = async () => {
    if (!auditPlayerName.trim() || !auditProp.trim()) {
      toast.error('Please provide player name and prop');
      return;
    }

    if (auditMode === 'manual' && !auditFinalStat.trim()) {
      toast.error('Please provide the final stat for manual verification');
      return;
    }

    if (auditMode === 'screenshot' && !auditScreenshot) {
      toast.error('Please paste or upload a screenshot of the player stats');
      return;
    }

    setAuditingResult(true);
    try {
      let response;
      
      if (auditMode === 'screenshot') {
        // Use screenshot-based verification
        response = await axios.post(
          `${API}/command-center/audit-result-screenshot`,
          {
            image_base64: auditScreenshot,
            player_name: auditPlayerName,
            prop: auditProp
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (auditMode === 'auto') {
        // Use ESPN auto-fetch
        response = await axios.post(
          `${API}/command-center/audit-result-auto`,
          {
            player_name: auditPlayerName,
            prop: auditProp
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!response.data.success) {
          toast.error(response.data.error || 'Auto-fetch failed. Try Screenshot or Manual mode.');
          setAuditingResult(false);
          return;
        }
      } else {
        // Use manual mode
        response = await axios.post(
          `${API}/command-center/audit-result`,
          {
            player_name: auditPlayerName,
            prop: auditProp,
            final_stat: parseFloat(auditFinalStat),
            mode: 'manual'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        setAuditResult(response.data);
        const message = response.data.message || `Result: ${response.data.audit.result}`;
        toast.success(message);
        fetchAuditData();
        // Clear form
        setAuditPlayerName('');
        setAuditProp('');
        setAuditFinalStat('');
        setAuditScreenshot(null);
      }
    } catch (error) {
      toast.error('Failed to audit result: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAuditingResult(false);
    }
  };

  // Handle paste for audit screenshot
  const handleAuditPaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          const reader = new FileReader();
          reader.onload = (event) => {
            setAuditScreenshot(event.target.result);
            toast.success('Screenshot pasted! Now click Verify Result.');
          };
          reader.readAsDataURL(file);
          break;
        }
      }
    }
  };

  // Handle file upload for audit screenshot
  const handleAuditFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAuditScreenshot(event.target.result);
        toast.success('Screenshot uploaded! Now click Verify Result.');
      };
      reader.readAsDataURL(file);
    }
  };


  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="command-center">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Brain className="text-white" size={24} />
            </div>
            <h1 className="text-4xl font-heading font-bold">PSL COMMAND CENTER</h1>
          </div>
          <p className="text-muted-foreground">Your daily content workflow automation hub</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-border pb-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('intelligence')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'intelligence' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-intelligence"
          >
            <Brain size={16} />
            Intelligence
          </button>
          <button
            onClick={() => setActiveTab('victorylap')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'victorylap' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-victorylap"
          >
            <Trophy size={16} />
            Victory Lap
          </button>
          <button
            onClick={() => setActiveTab('growth')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'growth' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-growth"
          >
            <TrendingUp size={16} />
            Growth
          </button>
          <button
            onClick={() => setActiveTab('twitter')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'twitter' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-twitter"
          >
            <Twitter size={16} />
            Twitter
          </button>
          <button
            onClick={() => setActiveTab('thumbnail')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'thumbnail' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-thumbnail"
          >
            <Image size={16} />
            Thumbnail
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'articles' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-articles"
          >
            <Newspaper size={16} />
            Articles
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'results' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-results"
          >
            <ClipboardCheck size={16} />
            Results
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'workflow' 
                ? 'bg-primary text-white' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-workflow"
          >
            <CheckCircle2 size={16} />
            Workflow
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-t transition-all whitespace-nowrap ${
              activeTab === 'tools' 
                ? 'bg-yellow-500 text-black' 
                : 'bg-surface hover:bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="tab-tools"
          >
            <Target size={16} />
            Tools
          </button>
        </div>

        {/* Intelligence Lab Tab */}
        {activeTab === 'intelligence' && (
          <div className="space-y-8">
            {/* Upload/Paste Zone */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Sparkles className="text-primary" size={20} />
                  Screenshot Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Drop Zone */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                      pastedImage 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-surface-highlight'
                    }`}
                    data-testid="drop-zone"
                  >
                    {pastedImage ? (
                      <div className="space-y-4">
                        <img 
                          src={pastedImage} 
                          alt="Screenshot preview" 
                          className="max-h-64 mx-auto rounded border border-border"
                        />
                        <div className="flex gap-2 justify-center">
                          <Button
                            onClick={analyzeImage}
                            disabled={analyzing}
                            className="bg-primary text-white"
                            data-testid="analyze-button"
                          >
                            {analyzing ? (
                              <>
                                <Loader2 className="animate-spin mr-2" size={16} />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Brain className="mr-2" size={16} />
                                Analyze Screenshot
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={clearImage}
                            variant="outline"
                            data-testid="clear-image-button"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 text-muted-foreground">
                          <div className="flex flex-col items-center">
                            <Clipboard size={32} className="mb-2" />
                            <span className="text-sm font-mono">CTRL+V to Paste</span>
                          </div>
                          <span className="text-2xl">or</span>
                          <div className="flex flex-col items-center">
                            <Upload size={32} className="mb-2" />
                            <span className="text-sm font-mono">Drag & Drop</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Paste a screenshot from Outlier or upload an image file
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="file-input"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          data-testid="upload-button"
                        >
                          <Upload className="mr-2" size={16} />
                          Browse Files
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Analysis Result Preview */}
                  <div className="bg-surface rounded-lg p-4 border border-border">
                    <h3 className="font-mono text-sm uppercase tracking-wider text-muted-foreground mb-4">
                      Analysis Output
                    </h3>
                    {analysisResult ? (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        <pre className="text-xs bg-surface-highlight p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
                          {analysisResult.raw_response}
                        </pre>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(analysisResult.raw_response)}
                          >
                            <Copy size={14} className="mr-2" />
                            Copy Output
                          </Button>
                          <Button
                            size="sm"
                            className="bg-primary text-white"
                            onClick={saveAsReport}
                          >
                            <FileText size={14} className="mr-2" />
                            Save as Report
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <BarChart3 size={40} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Analysis results will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reports List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-heading font-bold">Intelligence Reports</h2>
                <span className="text-sm text-muted-foreground font-mono">
                  {reports.length} reports
                </span>
              </div>
              
              {reports.length === 0 ? (
                <Card className="bg-card border-border">
                  <CardContent className="py-12 text-center">
                    <Brain size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No intelligence reports yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {reports.map((report) => (
                    <Card 
                      key={report.id} 
                      className={`bg-card border-border cursor-pointer transition-all hover:border-primary/50 ${
                        selectedReportsForScript.includes(report.id) ? 'border-primary ring-1 ring-primary' : ''
                      }`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              checked={selectedReportsForScript.includes(report.id)}
                              onChange={() => toggleReportSelection(report.id)}
                              className="w-5 h-5 rounded border-border"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold">{report.player_name}</span>
                                <span className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-mono">
                                  {report.prop_type}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                {report.edge_summary || 'No edge summary'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedReport(report);
                                setReportDialogOpen(true);
                              }}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteReport(report.id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {selectedReportsForScript.length > 0 && (
                <div className="mt-4 p-4 bg-surface-highlight border border-primary/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">
                      <span className="font-bold">{selectedReportsForScript.length}</span> report(s) selected
                    </span>
                    <Button
                      onClick={generateScript}
                      disabled={generatingScript}
                      className="bg-primary text-white"
                    >
                      {generatingScript ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2" size={16} />
                          Generate Script
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scripts Tab */}
        {activeTab === 'victorylap' && (
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 font-heading text-xl">
                      <Trophy className="text-primary" size={22} />
                      Victory Lap
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Generate post-game follow-up posts for X/Twitter and Threads
                    </p>
                  </div>
                  <Button
                    onClick={generateVictoryLap}
                    disabled={victoryLapLoading}
                    className="bg-primary text-white hover:bg-primary/90 font-bold"
                    data-testid="generate-victory-lap"
                  >
                    {victoryLapLoading ? (
                      <><Loader2 className="animate-spin mr-2" size={16} />Generating...</>
                    ) : (
                      <><Trophy className="mr-2" size={16} />Generate Follow-Ups</>
                    )}
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {victoryLapData && (
              <>
                {/* Tonight's Summary */}
                <div className="flex gap-4 text-center">
                  <Card className="bg-card border-border flex-1">
                    <CardContent className="py-4">
                      <div className="text-2xl font-bold text-primary">{victoryLapData.tonight_record}</div>
                      <div className="text-xs text-muted-foreground font-mono uppercase">Tonight</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border flex-1">
                    <CardContent className="py-4">
                      <div className="text-2xl font-bold">{victoryLapData.season_record}</div>
                      <div className="text-xs text-muted-foreground font-mono uppercase">Season</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border flex-1">
                    <CardContent className="py-4">
                      <div className="text-2xl font-bold text-primary">{victoryLapData.win_percentage}%</div>
                      <div className="text-xs text-muted-foreground font-mono uppercase">Win Rate</div>
                    </CardContent>
                  </Card>
                </div>

                {/* X/Twitter Single Post */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 font-heading text-lg">
                        <Twitter className="text-[#1DA1F2]" size={18} />
                        X / Twitter Post
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(victoryLapData.twitter_post)} data-testid="copy-twitter-post">
                        <Copy size={14} className="mr-1" /> Copy
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-surface rounded-lg p-4 whitespace-pre-wrap text-sm font-mono">
                      {victoryLapData.twitter_post}
                    </div>
                  </CardContent>
                </Card>

                {/* X/Twitter Thread */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 font-heading text-lg">
                        <Twitter className="text-[#1DA1F2]" size={18} />
                        X / Twitter Thread
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(victoryLapData.twitter_thread)} data-testid="copy-twitter-thread">
                        <Copy size={14} className="mr-1" /> Copy All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {victoryLapData.twitter_thread
                      .split(/\n(?=\d+\/\d+)/)
                      .map(t => t.trim())
                      .filter(t => t.length > 0)
                      .map((tweet, i) => (
                        <div key={i} className="bg-surface rounded-lg p-4 relative group">
                          <div className="flex items-start justify-between gap-3">
                            <div className="whitespace-pre-wrap text-sm font-mono flex-1">{tweet}</div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => copyToClipboard(tweet)}
                              className="shrink-0 opacity-70 group-hover:opacity-100"
                              data-testid={`copy-thread-${i + 1}`}
                            >
                              <Copy size={14} />
                            </Button>
                          </div>
                        </div>
                      ))
                    }
                  </CardContent>
                </Card>

                {/* Threads Post */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 font-heading text-lg">
                        <MessageSquare size={18} />
                        Threads Post
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(victoryLapData.threads_post)} data-testid="copy-threads-post">
                        <Copy size={14} className="mr-1" /> Copy
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-surface rounded-lg p-4 whitespace-pre-wrap text-sm font-mono">
                      {victoryLapData.threads_post}
                    </div>
                  </CardContent>
                </Card>

                {/* Affiliate Links Reference */}
                <div className="flex gap-3 justify-center pt-2">
                  <a href="https://prizepicks.onelink.me/FjtC/d9ee5h8t" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary transition-colors">PrizePicks</a>
                  <span className="text-muted-foreground/30">|</span>
                  <a href="https://engagebetr.onelink.me/auSX/STATLAB" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary transition-colors">Betr</a>
                  <span className="text-muted-foreground/30">|</span>
                  <a href="https://novig.onelink.me/JHQQ/5wk3auau" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary transition-colors">NoVig</a>
                </div>
              </>
            )}

            {!victoryLapData && !victoryLapLoading && (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Trophy size={48} className="mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground mb-2">No follow-ups generated yet</p>
                  <p className="text-sm text-muted-foreground">
                    After tonight's games, run "Auto-Update Results" in the Admin Dashboard, then come here to generate your follow-up posts.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Growth Lab Tab */}
        {activeTab === 'growth' && (
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <TrendingUp className="text-primary" size={20} />
                  Growth Lab
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Strategic review of your script: angle evaluation, suggestions, title options, and retention check
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Script Selection */}
                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider mb-3 block">
                    Select a Script to Review
                  </Label>
                  {scripts.length === 0 ? (
                    <div className="text-center py-8 bg-surface rounded-lg border border-border">
                      <FileText size={40} className="mx-auto mb-2 text-muted-foreground opacity-50" />
                      <p className="text-sm text-muted-foreground">No scripts available</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Generate a script first from the Intelligence Lab
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {scripts.map((script) => (
                        <button
                          key={script.id}
                          onClick={() => setSelectedScriptForGrowth(script)}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            selectedScriptForGrowth?.id === script.id
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`growth-script-${script.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold">Script - {script.prop_count} Prop(s)</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {script.duration_target} • {new Date(script.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            {selectedScriptForGrowth?.id === script.id && (
                              <CheckCircle2 className="text-primary" size={20} />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Generate Button */}
                {selectedScriptForGrowth && (
                  <Button
                    onClick={() => generateGrowthReview(selectedScriptForGrowth.id)}
                    disabled={generatingGrowth}
                    className="bg-primary text-white"
                    data-testid="generate-growth-review-button"
                  >
                    {generatingGrowth ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={16} />
                        Generating Review...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="mr-2" size={16} />
                        Generate Growth Review
                      </>
                    )}
                  </Button>
                )}

                {/* Growth Review Output */}
                {growthReview && (
                  <div className="mt-6 space-y-4">
                    {/* Quick Copy All Banner */}
                    <div className="bg-gradient-to-r from-primary/20 to-accent/20 p-4 rounded-lg border border-primary/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-lg flex items-center gap-2">
                            <Download size={18} className="text-primary" />
                            Ready to Export
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Copy title + script + review in one click
                          </p>
                        </div>
                        <Button
                          onClick={copyAllGrowthContent}
                          className="bg-primary text-white hover:bg-primary/90"
                          data-testid="quick-copy-all-button"
                        >
                          <Copy size={16} className="mr-2" />
                          Quick Copy All
                        </Button>
                      </div>
                    </div>

                    {/* Title Options with Selection */}
                    {growthReview.titles?.title_options && growthReview.titles.title_options.length > 0 && (
                      <div className="bg-surface p-4 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                            Title Options (click to select for export)
                          </Label>
                          <span className="text-xs text-primary font-mono">
                            Selected: #{selectedTitleIndex + 1}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {growthReview.titles.title_options.map((title, idx) => (
                            <div 
                              key={idx} 
                              onClick={() => setSelectedTitleIndex(idx)}
                              className={`flex items-center justify-between p-3 rounded cursor-pointer transition-all ${
                                selectedTitleIndex === idx 
                                  ? 'bg-primary/20 border-2 border-primary' 
                                  : 'bg-surface-highlight hover:bg-surface-highlight/80 border-2 border-transparent'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  selectedTitleIndex === idx ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                                }`}>
                                  {idx + 1}
                                </span>
                                <span className="text-sm">{title}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(title); }}
                              >
                                <Copy size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Review */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-mono text-sm uppercase tracking-wider">
                          Full 4-Block Review
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(growthReview.review)}
                        >
                          <Copy size={14} className="mr-2" />
                          Copy Full Review
                        </Button>
                      </div>
                      <div className="bg-surface rounded-lg p-4 max-h-96 overflow-y-auto border border-border">
                        <pre className="text-sm whitespace-pre-wrap font-mono">
                          {growthReview.review}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Twitter Repurpose Tab */}
        {activeTab === 'twitter' && (
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Twitter className="text-primary" size={20} />
                  Twitter Repurpose Lab
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Paste your YouTube video transcript to convert it into a Twitter article
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider">
                    YouTube Transcript
                  </Label>
                  <Textarea
                    value={twitterTranscript}
                    onChange={(e) => setTwitterTranscript(e.target.value)}
                    placeholder="Paste your YouTube video transcript here..."
                    className="mt-2 min-h-[200px] font-mono text-sm"
                    data-testid="twitter-transcript-input"
                  />
                </div>
                <Button
                  onClick={generateTwitterArticle}
                  disabled={generatingTwitter || !twitterTranscript.trim()}
                  className="bg-primary text-white"
                  data-testid="generate-twitter-button"
                >
                  {generatingTwitter ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Twitter className="mr-2" size={16} />
                      Generate Twitter Article
                    </>
                  )}
                </Button>

                {twitterArticle && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-mono text-sm uppercase tracking-wider">
                        Generated Article
                      </Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(twitterArticle)}
                      >
                        <Copy size={14} className="mr-2" />
                        Copy
                      </Button>
                    </div>
                    <div className="bg-surface rounded-lg p-4 max-h-96 overflow-y-auto border border-border">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
                        {twitterArticle}
                      </pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Thumbnail Lab Tab - Enhanced Version */}
        {activeTab === 'thumbnail' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Controls */}
              <div className="space-y-4">
                {/* Intelligence Report Parser */}
                <Card className="bg-primary/10 border-primary/30">
                  <CardContent className="py-4">
                    <Button
                      onClick={() => setShowThumbnailReportParser(!showThumbnailReportParser)}
                      className="w-full bg-primary text-black hover:bg-primary/90"
                    >
                      <FileText size={18} className="mr-2" />
                      Paste Intelligence Report (Auto-Fill)
                      <Sparkles size={18} className="ml-2" />
                    </Button>
                    {thumbnailParseStatus && (
                      <p className="text-sm mt-3 text-center">{thumbnailParseStatus}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Report Parser Modal */}
                {showThumbnailReportParser && (
                  <Card className="bg-card border-border">
                    <CardContent className="py-4 space-y-3">
                      <h3 className="font-heading font-bold">Paste Your Intelligence Report</h3>
                      <Textarea
                        value={thumbnailReportText}
                        onChange={(e) => setThumbnailReportText(e.target.value)}
                        placeholder="Paste your SLATE INTELLIGENCE MEMO here..."
                        className="min-h-[150px] font-mono text-sm"
                      />
                      <div className="flex gap-3">
                        <Button onClick={parseThumbnailReport} className="flex-1 bg-primary text-black">
                          Extract Data
                        </Button>
                        <Button onClick={() => setShowThumbnailReportParser(false)} variant="outline">
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 font-heading">
                      <Image className="text-primary" size={20} />
                      Thumbnail Lab
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Style Selector */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Style</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {['3', '4', '5', '8'].map((s) => (
                          <button
                            key={s}
                            onClick={() => setThumbnailStyle(s)}
                            className={`p-2 border-2 transition-all rounded ${thumbnailStyle === s ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}
                          >
                            <div className="font-bold text-sm">Style {s}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {s === '3' ? 'Player Focus' : s === '4' ? 'Player Right' : s === '5' ? 'Player Left' : 'Bill Style'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Day Selector */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Day Badge</Label>
                      <select 
                        value={thumbnailSubtext}
                        onChange={(e) => setThumbnailSubtext(e.target.value)}
                        className="w-full bg-surface border border-border p-3 rounded text-foreground"
                      >
                        {thumbnailDays.map((day) => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>

                    {/* Headline */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Headline</Label>
                      <input
                        type="text"
                        value={thumbnailHeadline}
                        onChange={(e) => setThumbnailHeadline(e.target.value)}
                        placeholder="FREE PICKS"
                        className="w-full bg-surface border border-border p-3 rounded text-foreground"
                      />
                    </div>

                    {/* Player Name with Search */}
                    <div className="relative">
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Player Name</Label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={thumbnailPlayerSearch}
                            onChange={(e) => {
                              setThumbnailPlayerSearch(e.target.value);
                              setThumbnailPlayer(e.target.value.toUpperCase());
                              setShowThumbnailPlayerDropdown(true);
                            }}
                            onFocus={() => setShowThumbnailPlayerDropdown(true)}
                            placeholder="Search NBA players..."
                            className="w-full bg-surface border border-border p-3 rounded text-foreground"
                          />
                          {showThumbnailPlayerDropdown && thumbnailPlayerSearch && filteredThumbnailPlayers.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded max-h-48 overflow-y-auto">
                              {filteredThumbnailPlayers.map((p) => (
                                <button
                                  key={p}
                                  onClick={() => selectThumbnailPlayer(p)}
                                  className="w-full text-left px-4 py-2 hover:bg-primary/10 transition-colors"
                                >
                                  {p}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={searchThumbnailPlayerImages}
                          disabled={!thumbnailPlayer}
                          className="bg-primary text-black"
                        >
                          <Image size={18} className="mr-1" />
                          Find
                        </Button>
                      </div>
                    </div>

                    {/* Stat/Line */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Stat/Line</Label>
                      <input
                        type="text"
                        value={thumbnailStat}
                        onChange={(e) => setThumbnailStat(e.target.value)}
                        placeholder="Under 23.5 PTS+AST"
                        className="w-full bg-surface border border-border p-3 rounded text-foreground"
                      />
                    </div>

                    {/* Image URL and Upload */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Player Image</Label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={thumbnailImageUrl}
                          onChange={(e) => setThumbnailImageUrl(e.target.value)}
                          placeholder="Paste image URL here..."
                          className="flex-1 bg-surface border border-border p-3 rounded text-foreground text-sm"
                        />
                        <label className="bg-primary text-black px-4 py-2 rounded cursor-pointer hover:bg-primary/90 flex items-center gap-2 font-bold">
                          <Upload size={18} />
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = async (event) => {
                                  const dataUrl = event.target.result;
                                  setThumbnailImageUrl(dataUrl);
                                  
                                  // Also upload to server to get a short URL for the full-size page
                                  try {
                                    const response = await axios.post(`${API}/temp-image`, {
                                      image_data: dataUrl
                                    });
                                    // Store the server URL for use in getThumbnailPreviewUrl
                                    setThumbnailImageServerId(response.data.image_id);
                                  } catch (err) {
                                    console.error('Failed to upload temp image:', err);
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Upload from your computer or paste a URL. Use <a href="https://remove.bg" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">remove.bg</a> to remove backgrounds
                      </p>
                    </div>

                    {/* Image Effects */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Image Effects</Label>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setThumbnailImageEffect('none')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailImageEffect === 'none' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          None
                        </button>
                        <button
                          onClick={() => setThumbnailImageEffect('glow')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailImageEffect === 'glow' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          Green Glow
                        </button>
                        <button
                          onClick={() => setThumbnailImageEffect('shadow')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailImageEffect === 'shadow' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          Drop Shadow
                        </button>
                        <button
                          onClick={() => setThumbnailImageEffect('intense')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailImageEffect === 'intense' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          Intense
                        </button>
                      </div>
                    </div>

                    {/* Output Size */}
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Output Size</Label>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setThumbnailOutputSize('youtube')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailOutputSize === 'youtube' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          YouTube (1280x720)
                        </button>
                        <button
                          onClick={() => setThumbnailOutputSize('twitter')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailOutputSize === 'twitter' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          Twitter (1200x675)
                        </button>
                        <button
                          onClick={() => setThumbnailOutputSize('website')}
                          className={`px-3 py-2 border rounded text-sm ${thumbnailOutputSize === 'website' ? 'border-primary bg-primary/10' : 'border-border'}`}
                        >
                          Website (No Logo)
                        </button>
                      </div>
                    </div>

                    {/* Sponsor Logo */}
                    {thumbnailOutputSize !== 'website' && (
                      <div>
                        <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Sponsor Logo</Label>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => setThumbnailSponsorLogo('prizepicks')}
                            className={`px-3 py-2 border rounded text-sm flex items-center gap-2 ${thumbnailSponsorLogo === 'prizepicks' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            <img src={sponsorLogos.prizepicks} alt="PrizePicks" className="h-5 w-auto" />
                            PrizePicks
                          </button>
                          <button
                            onClick={() => setThumbnailSponsorLogo('prizepicks_light')}
                            className={`px-3 py-2 border rounded text-sm flex items-center gap-2 ${thumbnailSponsorLogo === 'prizepicks_light' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            <img src={sponsorLogos.prizepicks_light} alt="PrizePicks Light" className="h-5 w-auto" />
                            PP Light
                          </button>
                          <button
                            onClick={() => setThumbnailSponsorLogo('novig')}
                            className={`px-3 py-2 border rounded text-sm flex items-center gap-2 ${thumbnailSponsorLogo === 'novig' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            <img src={sponsorLogos.novig} alt="Novig" className="h-5 w-auto" />
                            Novig
                          </button>
                          <button
                            onClick={() => setThumbnailSponsorLogo('none')}
                            className={`px-3 py-2 border rounded text-sm ${thumbnailSponsorLogo === 'none' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            None
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Logo Position */}
                    {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && (
                      <div>
                        <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">Logo Position</Label>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => setThumbnailLogoPosition('bottom-right')}
                            className={`px-3 py-2 border rounded text-sm ${thumbnailLogoPosition === 'bottom-right' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            Bottom Right
                          </button>
                          <button
                            onClick={() => setThumbnailLogoPosition('bottom-left')}
                            className={`px-3 py-2 border rounded text-sm ${thumbnailLogoPosition === 'bottom-left' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            Bottom Left
                          </button>
                          <button
                            onClick={() => setThumbnailLogoPosition('top-right')}
                            className={`px-3 py-2 border rounded text-sm ${thumbnailLogoPosition === 'top-right' ? 'border-primary bg-primary/10' : 'border-border'}`}
                          >
                            Top Right
                          </button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Image Search Results */}
                {showThumbnailImageSearch && (
                  <Card className="bg-card border-primary">
                    <CardContent className="py-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-heading font-bold">Find {thumbnailPlayer} Images</h3>
                        <Button variant="ghost" size="sm" onClick={() => setShowThumbnailImageSearch(false)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Click a source, find an image, right-click → "Copy Image Address", paste above.
                      </p>
                      <div className="space-y-2">
                        {thumbnailImageResults.map((source, i) => (
                          <a
                            key={i}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 border border-border hover:border-primary hover:bg-primary/5 rounded transition-all"
                          >
                            <div>
                              <div className="font-bold text-primary">{source.name}</div>
                              <div className="text-xs text-muted-foreground">{source.description}</div>
                            </div>
                            <ExternalLink size={16} className="text-muted-foreground" />
                          </a>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Live Stats */}
                <Card className="bg-card border-border">
                  <CardContent className="py-4">
                    <h3 className="font-heading font-bold mb-3">Your Live Stats</h3>
                    <div className="flex gap-6">
                      <div>
                        <div className="text-2xl font-bold text-primary">{thumbnailWinRate}%</div>
                        <div className="text-xs text-muted-foreground">Win Rate</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{thumbnailRecord.wins}-{thumbnailRecord.losses}</div>
                        <div className="text-xs text-muted-foreground">Record</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Get Thumbnail Button */}
                <Card className="bg-card border-border">
                  <CardContent className="py-4">
                    <a 
                      href={getThumbnailPreviewUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-primary text-black font-bold py-3 px-6 rounded hover:bg-primary/90 transition-colors"
                    >
                      <ExternalLink size={18} />
                      Open Full-Size Thumbnail
                    </a>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Right-click → Save Image, or take a screenshot
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Preview */}
              <div>
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="font-heading">Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-black border border-border rounded overflow-hidden" style={{ aspectRatio: '16/9' }}>
                      <div className="w-full h-full relative overflow-hidden">
                        {thumbnailStyle === '8' ? (
                          <div className="w-full h-full bg-black relative overflow-hidden flex">
                            {/* Player Image - MASSIVE */}
                            {thumbnailImageUrl && (
                              <div className="absolute z-10" style={{ height: '280%', bottom: '-120px', left: '-140px' }}>
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain object-bottom" style={getImageEffectStyle()} />
                              </div>
                            )}
                            {/* LEFT SIDE - Player info */}
                            <div className="w-[65%] h-full relative z-20">
                              <div className="absolute top-2 left-2">
                                {thumbnailPlayer && <div className="text-lg font-heading font-black text-white">{thumbnailPlayer}</div>}
                                {thumbnailStat && <div className="text-sm text-primary font-heading">{thumbnailStat}</div>}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-base font-black text-primary">{thumbnailWinRate}%</span>
                                  <span className="text-sm font-bold text-white">{thumbnailRecord.wins}-{thumbnailRecord.losses}</span>
                                </div>
                              </div>
                              <div className="absolute bottom-2 left-2">
                                <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                                  <span className="text-black font-bold text-[8px]">PSL</span>
                                </div>
                              </div>
                            </div>
                            {/* RIGHT SIDE - Green box centered */}
                            <div className="w-[35%] h-full bg-primary flex flex-col justify-center items-center relative z-20">
                              <div className="absolute top-2 left-0 right-0 text-center">
                                <span className="text-black text-sm font-black uppercase">{thumbnailSubtext}</span>
                              </div>
                              <div className="text-center">
                                <h1 className="font-heading font-black text-black leading-[0.85] text-4xl">{thumbnailHeadline.split(' ')[0]}</h1>
                                <h1 className="font-heading font-black text-white leading-[0.85] text-4xl" style={{ textShadow: '2px 2px 0px rgba(0,0,0,0.3)' }}>{thumbnailHeadline.split(' ').slice(1).join(' ')}</h1>
                              </div>
                              {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                                <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                                  <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-6 w-auto" />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : thumbnailStyle === '5' ? (
                          <div className="w-full h-full bg-gradient-to-r from-black via-gray-900 to-black relative flex">
                            {/* FREE PICKS Badge - Top Right */}
                            <div className="absolute top-3 right-3 z-30">
                              <span className="bg-primary text-black px-3 py-1 text-xs font-black uppercase tracking-wider shadow-lg">
                                FREE PICKS
                              </span>
                            </div>
                            {/* Player Image - LEFT side */}
                            {thumbnailImageUrl && (
                              <div className="absolute left-0 bottom-0 h-full flex items-end z-10">
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain object-bottom" style={getImageEffectStyle()} />
                              </div>
                            )}
                            {/* Right Side Content - All the way to the right edge */}
                            <div className="absolute right-0 top-0 h-full w-[50%] flex flex-col justify-center items-end pr-4 text-right z-20">
                              <div className="mb-2">
                                <span className="bg-primary text-black px-3 py-1 text-xs font-bold uppercase tracking-widest">{thumbnailSubtext}</span>
                              </div>
                              {thumbnailPlayer && <h1 className="text-2xl md:text-3xl font-heading font-black text-white leading-tight mb-1 text-right">{thumbnailPlayer}</h1>}
                              {thumbnailStat && <div className="text-lg md:text-xl font-heading text-primary mb-2 text-right">{thumbnailStat}</div>}
                              <h2 className="text-xl md:text-2xl font-heading font-black mb-3 text-right" style={{ textShadow: '0 0 20px rgba(74,222,128,0.8), 0 0 40px rgba(74,222,128,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}>
                                <span className="text-primary">{thumbnailHeadline.split(' ')[0]}</span>
                                <span className="text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}> {thumbnailHeadline.split(' ').slice(1).join(' ')}</span>
                              </h2>
                              <div className="flex gap-3">
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-primary">{thumbnailWinRate}%</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Win Rate</div>
                                </div>
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-white">{thumbnailRecord.wins}-{thumbnailRecord.losses}</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Record</div>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-gray-400 font-mono text-[10px]">PRIMESTATLAB.COM</span>
                                <div className="w-5 h-5 bg-primary rounded flex items-center justify-center">
                                  <span className="text-black font-bold text-[10px]">PSL</span>
                                </div>
                              </div>
                            </div>
                            {/* Sponsor Logo - Bottom LEFT */}
                            {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                              <div className="absolute bottom-3 left-3 z-30">
                                <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-8 w-auto" />
                              </div>
                            )}
                          </div>
                        ) : thumbnailStyle === '4' ? (
                          <div className="w-full h-full bg-gradient-to-l from-black via-gray-900 to-black relative flex">
                            {/* FREE PICKS Badge - Top Right */}
                            <div className="absolute top-3 right-3 z-30">
                              <span className="bg-primary text-black px-3 py-1 text-xs font-black uppercase tracking-wider shadow-lg">
                                FREE PICKS
                              </span>
                            </div>
                            {/* Player Image - RIGHT side */}
                            {thumbnailImageUrl && (
                              <div className="absolute right-0 bottom-0 h-full flex items-end z-10">
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain object-bottom" style={getImageEffectStyle()} />
                              </div>
                            )}
                            {/* Left Side Content */}
                            <div className="absolute left-0 top-0 h-full w-[55%] flex flex-col justify-center pl-6 z-20">
                              <div className="mb-2">
                                <span className="bg-primary text-black px-3 py-1 text-xs font-bold uppercase tracking-widest">{thumbnailSubtext}</span>
                              </div>
                              {thumbnailPlayer && <h1 className="text-2xl md:text-3xl font-heading font-black text-white leading-tight mb-1">{thumbnailPlayer}</h1>}
                              {thumbnailStat && <div className="text-lg md:text-xl font-heading text-primary mb-2">{thumbnailStat}</div>}
                              <h2 className="text-xl md:text-2xl font-heading font-black mb-3" style={{ textShadow: '0 0 20px rgba(74,222,128,0.8), 0 0 40px rgba(74,222,128,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}>
                                <span className="text-primary">{thumbnailHeadline.split(' ')[0]}</span>
                                <span className="text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}> {thumbnailHeadline.split(' ').slice(1).join(' ')}</span>
                              </h2>
                              <div className="flex gap-3">
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-primary">{thumbnailWinRate}%</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Win Rate</div>
                                </div>
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-white">{thumbnailRecord.wins}-{thumbnailRecord.losses}</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Record</div>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <div className="w-5 h-5 bg-primary rounded flex items-center justify-center">
                                  <span className="text-black font-bold text-[10px]">PSL</span>
                                </div>
                                <span className="text-gray-400 font-mono text-[10px]">PRIMESTATLAB.COM</span>
                              </div>
                            </div>
                            {/* Sponsor Logo */}
                            {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                              <div className={`absolute z-30 ${
                                thumbnailLogoPosition === 'bottom-right' ? 'bottom-3 right-3' :
                                thumbnailLogoPosition === 'bottom-left' ? 'bottom-3 left-3' :
                                'top-3 left-3'
                              }`}>
                                <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-8 w-auto" />
                              </div>
                            )}
                          </div>
                        ) : thumbnailStyle === '3' ? (
                          <div className="w-full h-full bg-gradient-to-r from-black via-gray-900 to-black relative flex">
                            {/* FREE PICKS Badge - Always Visible */}
                            <div className="absolute top-3 left-3 z-30">
                              <span className="bg-primary text-black px-3 py-1 text-xs font-black uppercase tracking-wider shadow-lg">
                                FREE PICKS
                              </span>
                            </div>
                            
                            {thumbnailImageUrl && (
                              <div className="absolute left-0 bottom-0 h-full flex items-end z-10">
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain object-bottom" style={getImageEffectStyle()} />
                              </div>
                            )}
                            <div className="absolute right-0 top-0 h-full w-[55%] flex flex-col justify-center pr-6 z-20">
                              <div className="mb-2">
                                <span className="bg-primary text-black px-3 py-1 text-xs font-bold uppercase tracking-widest">{thumbnailSubtext}</span>
                              </div>
                              {thumbnailPlayer && <h1 className="text-2xl md:text-3xl font-heading font-black text-white leading-tight mb-1">{thumbnailPlayer}</h1>}
                              {thumbnailStat && <div className="text-lg md:text-xl font-heading text-primary mb-2">{thumbnailStat}</div>}
                              <h2 className="text-xl md:text-2xl font-heading font-black mb-3" style={{ textShadow: '0 0 20px rgba(74,222,128,0.8), 0 0 40px rgba(74,222,128,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}>
                                <span className="text-primary">{thumbnailHeadline.split(' ')[0]}</span>
                                <span className="text-white" style={{ textShadow: '0 0 10px rgba(255,255,255,0.5), 2px 2px 4px rgba(0,0,0,0.9)' }}> {thumbnailHeadline.split(' ').slice(1).join(' ')}</span>
                              </h2>
                              <div className="flex gap-3">
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-primary">{thumbnailWinRate}%</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Win Rate</div>
                                </div>
                                <div className="bg-black/50 border border-primary/30 px-3 py-1">
                                  <div className="text-lg font-black text-white">{thumbnailRecord.wins}-{thumbnailRecord.losses}</div>
                                  <div className="text-gray-500 font-mono text-[8px] uppercase">Record</div>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <div className="w-5 h-5 bg-primary rounded flex items-center justify-center">
                                  <span className="text-black font-bold text-[10px]">PSL</span>
                                </div>
                                <span className="text-gray-400 font-mono text-[10px]">PRIMESTATLAB.COM</span>
                              </div>
                            </div>
                            <div className="absolute inset-0 opacity-30 pointer-events-none">
                              <div className="absolute top-0 right-1/4 w-48 h-48 bg-primary rounded-full blur-[80px]"></div>
                            </div>
                            {/* Sponsor Logo */}
                            {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                              <div className={`absolute z-30 ${
                                thumbnailLogoPosition === 'bottom-right' ? 'bottom-3 right-3' :
                                thumbnailLogoPosition === 'bottom-left' ? 'bottom-3 left-3' :
                                'top-3 right-3'
                              }`}>
                                <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-8 w-auto" />
                              </div>
                            )}
                          </div>
                        ) : thumbnailStyle === '2' ? (
                          <div className="w-full h-full bg-black relative overflow-hidden">
                            {/* FREE PICKS Badge - Top Left */}
                            <div className="absolute top-2 left-2 z-30">
                              <span className="bg-primary text-black px-2 py-0.5 text-[8px] font-black uppercase tracking-wider shadow-lg">
                                FREE PICKS
                              </span>
                            </div>
                            {/* Top Right - Logo */}
                            <div className="absolute top-2 right-2 z-30 flex items-center gap-1">
                              <span className="text-white font-heading text-[8px]">PRIMESTATLAB</span>
                              <div className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                                <span className="text-black font-bold text-[6px]">PSL</span>
                              </div>
                            </div>
                            {/* Above Player - Day Badge & Player Name */}
                            <div className="absolute top-8 left-1/2 -translate-x-1/2 z-30 text-center">
                              <span className="bg-primary text-black px-2 py-0.5 text-[8px] font-bold uppercase">{thumbnailSubtext}</span>
                              {thumbnailPlayer && <div className="text-sm font-heading text-white mt-1">{thumbnailPlayer}</div>}
                              {thumbnailStat && <div className="text-xs text-primary">{thumbnailStat}</div>}
                            </div>
                            {/* Stats BEHIND Player - Offset Right */}
                            <div className="absolute left-1/2 top-1/2 -translate-y-1/2 z-10" style={{ marginLeft: '40px' }}>
                              <div className="bg-black/80 border border-primary/50 p-2">
                                <div className="text-xl font-black text-primary text-center">{thumbnailWinRate}%</div>
                                <div className="text-[6px] text-gray-500 font-mono text-center">Win Rate</div>
                                <div className="text-lg font-black text-white text-center">{thumbnailRecord.wins}-{thumbnailRecord.losses}</div>
                              </div>
                            </div>
                            {/* Player Image - CENTER - fills from bottom */}
                            {thumbnailImageUrl && (
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20" style={{ height: '120%' }}>
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain" style={{ ...getImageEffectStyle(), objectPosition: 'center bottom' }} />
                              </div>
                            )}
                            {/* Sponsor Logo */}
                            {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                              <div className={`absolute z-30 ${
                                thumbnailLogoPosition === 'bottom-right' ? 'bottom-2 right-2' :
                                thumbnailLogoPosition === 'bottom-left' ? 'bottom-2 left-2' :
                                'top-8 right-2'
                              }`}>
                                <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-6 w-auto" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
                            {/* LEFT side - Logo, Headline, Tagline */}
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                              <div className="flex items-center gap-1 mb-2">
                                <div className="w-5 h-5 bg-primary rounded flex items-center justify-center">
                                  <span className="text-black font-bold text-[8px]">PSL</span>
                                </div>
                                <span className="text-white font-heading text-[10px]">PRIMESTATLAB</span>
                              </div>
                              <h1 className="text-xl font-heading font-black leading-none mb-1">
                                <span className="text-primary">{thumbnailHeadline.split(' ')[0]}</span><br/>
                                <span className="text-white">{thumbnailHeadline.split(' ').slice(1).join(' ')}</span>
                              </h1>
                              <p className="text-[8px] text-gray-400 font-mono leading-tight mb-1">Data-driven picks.<br/>No fluff, just stats.</p>
                              {thumbnailPlayer && <div className="text-xs font-heading text-white">{thumbnailPlayer}</div>}
                              {thumbnailStat && <div className="text-[10px] text-primary">{thumbnailStat}</div>}
                            </div>
                            {/* Player Image - CENTER - fills from bottom */}
                            {thumbnailImageUrl && (
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-20" style={{ height: '115%' }}>
                                <img src={thumbnailImageUrl} alt="Player" className="h-full w-auto object-contain" style={{ ...getImageEffectStyle(), objectPosition: 'center bottom' }} />
                              </div>
                            )}
                            {/* RIGHT side - Stats Card & Day Badge */}
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-right">
                              <div className="bg-black/70 border border-primary/50 p-2 mb-2">
                                <div className="text-xl font-black text-primary">{thumbnailWinRate}%</div>
                                <div className="text-[6px] text-gray-500 font-mono">Win Rate</div>
                                <div className="text-sm font-bold text-white">{thumbnailRecord.wins}W-{thumbnailRecord.losses}L-{thumbnailRecord.pending}P</div>
                              </div>
                              <span className="bg-primary text-black px-2 py-1 text-[8px] font-bold uppercase">{thumbnailSubtext}</span>
                            </div>
                            {/* Sponsor Logo */}
                            {thumbnailOutputSize !== 'website' && thumbnailSponsorLogo !== 'none' && sponsorLogos[thumbnailSponsorLogo] && (
                              <div className={`absolute z-30 ${
                                thumbnailLogoPosition === 'bottom-right' ? 'bottom-2 right-2' :
                                thumbnailLogoPosition === 'bottom-left' ? 'bottom-2 left-2' :
                                'top-2 right-2'
                              }`}>
                                <img src={sponsorLogos[thumbnailSponsorLogo]} alt="Sponsor" className="h-6 w-auto" />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <p className="text-sm text-yellow-500">
                        <strong>How to save:</strong> Click "Open Full-Size Thumbnail" then screenshot or right-click → Save Image
                      </p>
                    </div>

                    <div className="mt-4 bg-surface p-4 rounded border border-border">
                      <h4 className="font-heading font-bold mb-2">Quick Workflow</h4>
                      <ol className="text-sm text-muted-foreground space-y-1">
                        <li>1. Paste Intelligence Report OR search for player</li>
                        <li>2. Click "Find" → Choose an image source</li>
                        <li>3. Copy image URL → Paste in Image URL field</li>
                        <li>4. Click "Open Full-Size Thumbnail"</li>
                        <li>5. Screenshot → Upload to YouTube!</li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Articles Division Tab */}
        {activeTab === 'articles' && (
          <div className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Newspaper className="text-primary" size={20} />
                  Articles Division (Enhanced SEO)
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Merge Twitter + YouTube content into a comprehensive SEO article (900+ words)
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Optional Context Fields */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Player Name (optional)
                    </Label>
                    <input
                      type="text"
                      value={articlesPlayerName}
                      onChange={(e) => setArticlesPlayerName(e.target.value)}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., Alperen Sengun"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Opponent (optional)
                    </Label>
                    <input
                      type="text"
                      value={articlesOpponent}
                      onChange={(e) => setArticlesOpponent(e.target.value)}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., Charlotte Hornets"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Stat Type (optional)
                    </Label>
                    <input
                      type="text"
                      value={articlesStatType}
                      onChange={(e) => setArticlesStatType(e.target.value)}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., Rebounds"
                    />
                  </div>
                </div>

                {/* Content Inputs */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Twitter Article Content
                    </Label>
                    <Textarea
                      value={articlesTwitterContent}
                      onChange={(e) => setArticlesTwitterContent(e.target.value)}
                      placeholder="Paste your Twitter article here..."
                      className="mt-2 min-h-[200px] font-mono text-sm"
                      data-testid="articles-twitter-input"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      YouTube Script/Transcript
                    </Label>
                    <Textarea
                      value={articlesYoutubeContent}
                      onChange={(e) => setArticlesYoutubeContent(e.target.value)}
                      placeholder="Paste your YouTube script or transcript here..."
                      className="mt-2 min-h-[200px] font-mono text-sm"
                      data-testid="articles-youtube-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={generateSeoArticle}
                  disabled={generatingSeoArticle || !articlesTwitterContent.trim() || !articlesYoutubeContent.trim()}
                  className="bg-primary text-white"
                  data-testid="generate-seo-article-button"
                >
                  {generatingSeoArticle ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Generating SEO Article...
                    </>
                  ) : (
                    <>
                      <Newspaper className="mr-2" size={16} />
                      Generate SEO Article
                    </>
                  )}
                </Button>

                {seoArticle && (
                  <div className="mt-6 space-y-4">
                    {/* Featured Image */}
                    <div className="bg-surface p-4 rounded-lg border border-border">
                      <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        Featured Image (Optional)
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Upload from your computer or paste an image URL
                      </p>
                      
                      {/* Upload Button */}
                      <div className="flex gap-3 mb-3">
                        <label className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded cursor-pointer hover:bg-primary/90 transition-colors">
                          <Upload size={16} />
                          <span>Upload Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                const response = await axios.post(
                                  `${API}/upload-image`,
                                  formData,
                                  { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                                );
                                setArticleFeaturedImage(`${process.env.REACT_APP_BACKEND_URL}${response.data.url}`);
                                toast.success('Image uploaded!');
                              } catch (error) {
                                toast.error('Failed to upload image');
                              }
                            }}
                          />
                        </label>
                        <span className="text-muted-foreground self-center">or</span>
                      </div>
                      
                      {/* URL Input */}
                      <input
                        type="text"
                        value={articleFeaturedImage}
                        onChange={(e) => setArticleFeaturedImage(e.target.value)}
                        placeholder="Paste image URL here..."
                        className="w-full px-3 py-2 rounded border border-border bg-background text-foreground"
                      />
                      
                      {/* Preview */}
                      {articleFeaturedImage && (
                        <div className="mt-3">
                          <img 
                            src={articleFeaturedImage} 
                            alt="Preview" 
                            className="max-h-32 rounded border border-border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>

                    {/* SEO Metadata */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {seoArticle.seo_title && (
                        <div className="bg-surface p-4 rounded-lg border border-border">
                          <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                            SEO Title
                          </Label>
                          <input
                            type="text"
                            value={seoArticle.seo_title}
                            onChange={(e) => setSeoArticle({...seoArticle, seo_title: e.target.value})}
                            className="mt-1 w-full px-2 py-1 rounded border border-border bg-background text-foreground font-bold"
                          />
                        </div>
                      )}
                      <div className="bg-surface p-4 rounded-lg border border-border">
                        <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                          Word Count
                        </Label>
                        <p className="mt-1 font-bold text-2xl text-primary">{seoArticle.word_count}</p>
                      </div>
                    </div>

                    {seoArticle.meta_description && (
                      <div className="bg-surface p-4 rounded-lg border border-border">
                        <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                          Meta Description
                        </Label>
                        <input
                          type="text"
                          value={seoArticle.meta_description}
                          onChange={(e) => setSeoArticle({...seoArticle, meta_description: e.target.value})}
                          className="mt-1 w-full px-2 py-1 rounded border border-border bg-background text-foreground text-sm"
                        />
                      </div>
                    )}

                    {/* Editable Article Content */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-mono text-sm uppercase tracking-wider">
                          Generated Article (Editable)
                        </Label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(seoArticle.content)}
                          >
                            <Copy size={14} className="mr-2" />
                            Copy Article
                          </Button>
                          <Button
                            size="sm"
                            className="bg-accent text-white hover:bg-accent/90"
                            onClick={publishArticleToWebsite}
                            disabled={publishingArticle}
                            data-testid="publish-article-button"
                          >
                            {publishingArticle ? (
                              <>
                                <Loader2 className="animate-spin mr-2" size={14} />
                                Publishing...
                              </>
                            ) : (
                              <>
                                <ExternalLink size={14} className="mr-2" />
                                Publish to Website
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        value={seoArticle.content}
                        onChange={(e) => setSeoArticle({...seoArticle, content: e.target.value, word_count: e.target.value.split(/\s+/).filter(w => w).length})}
                        className="min-h-[400px] font-mono text-sm bg-surface border-border"
                        data-testid="seo-article-editor"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Edit the article above, then click "Publish to Website" to add it to your Articles page.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Article Rewrite Section */}
            <Card className="bg-card border-border mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <RefreshCw className="text-yellow-500" size={20} />
                  Rewrite Existing Article (SEO Format)
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Paste an existing article to restructure it into the hub-and-spoke SEO format
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider">
                    Date (optional - helps with formatting)
                  </Label>
                  <input
                    type="text"
                    value={rewriteDate}
                    onChange={(e) => setRewriteDate(e.target.value)}
                    className="mt-2 w-full max-w-xs px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                    placeholder="e.g., February 27, 2026"
                    data-testid="rewrite-date-input"
                  />
                </div>

                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider">
                    Existing Article Content
                  </Label>
                  <Textarea
                    value={rewriteContent}
                    onChange={(e) => setRewriteContent(e.target.value)}
                    placeholder="Paste your existing article here..."
                    className="mt-2 min-h-[200px] font-mono text-sm"
                    data-testid="rewrite-content-input"
                  />
                </div>

                <Button
                  onClick={rewriteExistingArticle}
                  disabled={rewritingArticle || !rewriteContent.trim()}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="rewrite-article-button"
                >
                  {rewritingArticle ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Rewriting Article...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2" size={16} />
                      Rewrite in SEO Format
                    </>
                  )}
                </Button>

                {/* Rewritten Article Output */}
                {rewrittenArticle && (
                  <div className="mt-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-heading font-bold text-yellow-500">
                        Rewritten Article ({rewrittenArticle.word_count} words)
                      </h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(rewrittenArticle.content);
                          toast.success('Copied to clipboard!');
                        }}
                        data-testid="copy-rewritten-button"
                      >
                        <Copy size={14} className="mr-1" /> Copy
                      </Button>
                    </div>
                    <Textarea
                      value={rewrittenArticle.content}
                      onChange={(e) => setRewrittenArticle({...rewrittenArticle, content: e.target.value})}
                      className="min-h-[400px] font-mono text-sm bg-surface border-border"
                      data-testid="rewritten-article-editor"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Results Audit Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            {/* Summary Stats */}
            {auditSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-primary">{auditSummary.total}</p>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Total</p>
                  </CardContent>
                </Card>
                <Card className="bg-accent/10 border-accent/30">
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-accent">{auditSummary.wins}</p>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Wins</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-red-500">{auditSummary.losses}</p>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Losses</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-500/10 border-yellow-500/30">
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-yellow-500">{auditSummary.pushes}</p>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Push</p>
                  </CardContent>
                </Card>
                <Card className="bg-primary/10 border-primary/30">
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-primary">{auditSummary.win_rate}%</p>
                    <p className="text-xs font-mono uppercase text-muted-foreground">Win Rate</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Audit Form */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <ClipboardCheck className="text-primary" size={20} />
                  Results Audit Lab
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Verify prior-day prop results: WIN / LOSS / PUSH / DNP
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mode Selection */}
                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider mb-3 block">
                    Verification Mode
                  </Label>
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => setAuditMode('screenshot')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        auditMode === 'screenshot'
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent/50'
                      }`}
                      data-testid="audit-mode-screenshot"
                    >
                      <p className="font-bold text-sm text-accent">Screenshot</p>
                      <p className="text-xs text-muted-foreground">Upload stats image</p>
                    </button>
                    <button
                      onClick={() => setAuditMode('auto')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        auditMode === 'auto'
                          ? 'border-secondary bg-secondary/10'
                          : 'border-border hover:border-secondary/50'
                      }`}
                      data-testid="audit-mode-auto"
                    >
                      <p className="font-bold text-sm text-secondary">Auto (ESPN)</p>
                      <p className="text-xs text-muted-foreground">Auto-fetch from ESPN</p>
                    </button>
                    <button
                      onClick={() => setAuditMode('manual')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        auditMode === 'manual'
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="audit-mode-manual"
                    >
                      <p className="font-bold text-sm">Manual</p>
                      <p className="text-xs text-muted-foreground">You type final stat</p>
                    </button>
                  </div>
                </div>

                {/* Auto Mode Info */}
                {auditMode === 'auto' && (
                  <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4">
                    <p className="text-sm text-secondary font-medium mb-1">Auto-Fetch Mode</p>
                    <p className="text-xs text-muted-foreground">
                      Automatically pulls the player's most recent game stats from ESPN. 
                      Just enter the player name and prop - no screenshot needed!
                    </p>
                  </div>
                )}

                {/* Screenshot Upload Zone - only show in screenshot mode */}
                {auditMode === 'screenshot' && (
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider mb-2 block">
                      Stats Screenshot
                    </Label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                        auditScreenshot 
                          ? 'border-accent bg-accent/5' 
                          : 'border-border hover:border-accent/50'
                      }`}
                      onPaste={handleAuditPaste}
                      onClick={() => document.getElementById('audit-file-input').click()}
                      tabIndex={0}
                      data-testid="audit-screenshot-zone"
                    >
                      <input
                        id="audit-file-input"
                        type="file"
                        accept="image/*"
                        onChange={handleAuditFileUpload}
                        className="hidden"
                      />
                      {auditScreenshot ? (
                        <div className="space-y-3">
                          <img 
                            src={auditScreenshot} 
                            alt="Stats screenshot" 
                            className="max-h-48 mx-auto rounded border border-border"
                          />
                          <p className="text-sm text-accent font-medium">Screenshot loaded!</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAuditScreenshot(null);
                            }}
                          >
                            Clear & Upload Different
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="mx-auto text-muted-foreground" size={32} />
                          <p className="text-sm text-muted-foreground">
                            <span className="font-bold text-foreground">Click to upload</span> or <span className="font-bold text-foreground">Ctrl+V to paste</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Upload ESPN/NBA.com stats screenshot
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Input Fields */}
                <div className={`grid gap-4 ${auditMode === 'manual' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Player Name
                    </Label>
                    <input
                      type="text"
                      value={auditPlayerName}
                      onChange={(e) => setAuditPlayerName(e.target.value)}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., Alperen Sengun"
                      data-testid="audit-player-input"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-sm uppercase tracking-wider">
                      Prop
                    </Label>
                    <input
                      type="text"
                      value={auditProp}
                      onChange={(e) => setAuditProp(e.target.value)}
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., Over 8.5 Rebounds"
                      data-testid="audit-prop-input"
                    />
                  </div>
                  {auditMode === 'manual' && (
                    <div>
                      <Label className="font-mono text-sm uppercase tracking-wider">
                        Final Stat
                      </Label>
                      <input
                        type="number"
                        step="0.1"
                        value={auditFinalStat}
                        onChange={(e) => setAuditFinalStat(e.target.value)}
                        className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                        placeholder="e.g., 2"
                        data-testid="audit-stat-input"
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={auditPropResult}
                  disabled={auditingResult || !auditPlayerName.trim() || !auditProp.trim() || (auditMode === 'screenshot' && !auditScreenshot) || (auditMode === 'manual' && !auditFinalStat.trim())}
                  className={auditMode === 'auto' ? "bg-secondary text-white hover:bg-secondary/90" : "bg-accent text-white hover:bg-accent/90"}
                  data-testid="audit-button"
                >
                  {auditingResult ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      {auditMode === 'auto' ? 'Fetching from ESPN...' : 'Verifying...'}
                    </>
                  ) : (
                    <>
                      <ClipboardCheck className="mr-2" size={16} />
                      {auditMode === 'auto' ? 'Auto-Verify from ESPN' : 'Verify Result'}
                    </>
                  )}
                </Button>

                {/* Latest Result */}
                {auditResult && (
                  <div className="mt-4 p-4 bg-surface rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-mono text-sm uppercase tracking-wider">
                        Verification Result
                      </Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(auditResult.raw_response)}
                      >
                        <Copy size={14} className="mr-2" />
                        Copy
                      </Button>
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-mono bg-surface-highlight p-3 rounded">
                      {auditResult.raw_response}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audit History */}
            {auditHistory.length > 0 && (
              <div>
                <h3 className="text-lg font-heading font-bold mb-4">Recent Audits</h3>
                <div className="space-y-2">
                  {auditHistory.slice(0, 10).map((audit) => (
                    <Card key={audit.id} className="bg-card border-border">
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className={`px-3 py-1 rounded font-mono text-sm font-bold ${
                              audit.result === 'WIN' ? 'bg-accent/20 text-accent' :
                              audit.result === 'LOSS' ? 'bg-red-500/20 text-red-500' :
                              audit.result === 'PUSH' ? 'bg-yellow-500/20 text-yellow-500' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {audit.result}
                            </span>
                            <div>
                              <p className="font-bold">{audit.player_name}</p>
                              <p className="text-sm text-muted-foreground">{audit.prop}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {audit.final_stat && (
                              <p className="font-mono">Final: {audit.final_stat}</p>
                            )}
                            {audit.difference && (
                              <p className="text-sm text-muted-foreground">{audit.difference}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Workflow Tab */}
        {activeTab === 'workflow' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-bold">Daily Workflow</h2>
                <p className="text-sm text-muted-foreground">
                  {workflow?.date || 'Today'} • {workflow?.completed_steps?.length || 0}/{workflow?.checklist_items?.length || 0} complete
                </p>
              </div>
            </div>

            {workflow ? (
              <div className="space-y-3">
                {workflow.checklist_items?.map((item, index) => {
                  const isComplete = workflow.completed_steps?.includes(item);
                  const isCurrent = workflow.current_step === item;
                  
                  return (
                    <Card 
                      key={index}
                      className={`bg-card border-border transition-all ${
                        isComplete 
                          ? 'border-accent/50 bg-accent/5' 
                          : isCurrent 
                            ? 'border-primary ring-1 ring-primary' 
                            : ''
                      }`}
                      data-testid={`workflow-step-${index}`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isComplete 
                                ? 'bg-accent text-white' 
                                : isCurrent 
                                  ? 'bg-primary text-white' 
                                  : 'bg-surface-highlight text-muted-foreground'
                            }`}>
                              {isComplete ? (
                                <CheckCircle2 size={18} />
                              ) : (
                                <span className="font-mono text-sm">{index + 1}</span>
                              )}
                            </div>
                            <span className={isComplete ? 'text-muted-foreground line-through' : ''}>
                              {item}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isComplete ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => uncompleteWorkflowStep(item)}
                                data-testid={`uncomplete-step-${index}`}
                              >
                                <Undo2 size={16} className="mr-2" />
                                Undo
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => completeWorkflowStep(item)}
                                data-testid={`complete-step-${index}`}
                              >
                                <CheckCircle2 size={16} className="mr-2" />
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Loader2 className="animate-spin mx-auto mb-4" size={32} />
                  <p className="text-muted-foreground">Loading workflow...</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tools Tab - Screenshot Capture */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                <Target className="text-yellow-500" />
                Screenshot Capture Tool
              </h2>
              <p className="text-sm text-muted-foreground">
                Capture screenshots from any website - Outlier, ESPN, etc.
              </p>
            </div>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Capture External Website</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-mono text-sm uppercase tracking-wider">
                    Website URL
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={screenshotUrl}
                      onChange={(e) => setScreenshotUrl(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-surface text-foreground"
                      placeholder="e.g., outlier.com or https://espn.com/nba"
                      data-testid="screenshot-url-input"
                    />
                    <Button
                      onClick={captureScreenshot}
                      disabled={capturingScreenshot || !screenshotUrl.trim()}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                      data-testid="capture-screenshot-button"
                    >
                      {capturingScreenshot ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Capturing...
                        </>
                      ) : (
                        <>
                          <Target className="mr-2" size={16} />
                          Capture
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Takes a few seconds to load and capture the page
                  </p>
                </div>

                {/* Captured Screenshot Preview */}
                {capturedScreenshot && (
                  <div className="mt-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-heading font-bold text-yellow-500">
                        Captured Screenshot
                      </h3>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(capturedScreenshot.image_url);
                            toast.success('Image URL copied!');
                          }}
                          data-testid="copy-screenshot-url"
                        >
                          <Copy size={14} className="mr-1" /> Copy URL
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(capturedScreenshot.image_url, '_blank')}
                          data-testid="open-screenshot"
                        >
                          <ExternalLink size={14} className="mr-1" /> Open Full Size
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Source: {capturedScreenshot.source_url}
                    </p>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <img 
                        src={capturedScreenshot.image_url} 
                        alt="Captured screenshot"
                        className="w-full"
                        data-testid="screenshot-preview"
                      />
                    </div>
                    <div className="bg-surface p-3 rounded">
                      <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        Image URL (use in thumbnails or articles)
                      </Label>
                      <code className="block mt-2 text-sm text-primary break-all">
                        {capturedScreenshot.image_url}
                      </code>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Report Detail Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {selectedReport?.player_name} - {selectedReport?.prop_type}
              </DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface p-4 rounded">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">PSL ID</Label>
                    <p className="font-bold mt-1">{selectedReport.psl_id || 'N/A'}</p>
                  </div>
                  <div className="bg-surface p-4 rounded">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Confidence</Label>
                    <p className="font-bold text-2xl text-primary mt-1">
                      {selectedReport.final_verdict?.confidence_score || 'N/A'}%
                    </p>
                  </div>
                </div>

                {selectedReport.edge_summary && (
                  <div className="bg-surface p-4 rounded">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Edge Summary</Label>
                    <p className="mt-1">{selectedReport.edge_summary}</p>
                  </div>
                )}

                {selectedReport.whats_missing && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded">
                    <Label className="font-mono text-xs uppercase tracking-wider text-yellow-500 flex items-center gap-2">
                      <AlertTriangle size={14} />
                      What's Missing
                    </Label>
                    <p className="mt-1">{selectedReport.whats_missing}</p>
                  </div>
                )}

                {selectedReport.risk_factors && selectedReport.risk_factors.length > 0 && (
                  <div className="bg-surface p-4 rounded">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Risk Factors</Label>
                    <ul className="mt-2 space-y-1">
                      {selectedReport.risk_factors.map((risk, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span className="text-loss">•</span>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(JSON.stringify(selectedReport, null, 2))}
                  >
                    <Copy size={16} className="mr-2" />
                    Copy JSON
                  </Button>
                  <Button onClick={() => setReportDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
